import {
  EntityChangeAction,
  EntityChangeActorType,
  IEntityChangeGroup
} from "../../interfaces/endpoints/IEntityChangeLog.types";
import {
  ACTION_LABELS,
  ACTION_STYLES,
  REVERT_LABEL,
  REVERT_STYLE,
  formatUtc
} from "./changeHistoryLabels";
import { changeLines } from "./changeSummary";
import { countedSentence } from "./changeSentences";
import { countedEntity } from "./entityCounts";

// Every row of the action was written by a revert: the action is an undo. An action whose rows are not
// all here cannot be judged, so it reads as the ordinary change it also is.
const allRowsHere = (group: IEntityChangeGroup): boolean => group.rows.length === group.rowCount;

const isUndo = (group: IEntityChangeGroup): boolean =>
  group.rows.length > 0 && allRowsHere(group) && group.rows.every((r) => !!r.revertOf);

// Every row of the action has since been undone: the action no longer stands.
export const wasUndone = (group: IEntityChangeGroup): boolean =>
  group.rows.length > 0 && allRowsHere(group) && group.rows.every((r) => !!r.revertedBy);

// The change this action undid, and the one that undid it: either opens the other side in the details.
export const undoneChangeId = (group: IEntityChangeGroup): number | null =>
  group.rows.find((r) => r.revertOf)?.revertOf?.logId ?? null;

export const undoingChangeId = (group: IEntityChangeGroup): number | null =>
  group.rows.find((r) => r.revertedBy)?.revertedBy?.logId ?? null;

const earliestUndone = (group: IEntityChangeGroup): string =>
  group.rows
    .map((r) => r.revertOf?.changedAtUtc ?? "")
    .filter(Boolean)
    .sort()[0];

// Who did it, from the pieces the list and the group both carry.
export const describeWho = (
  actorName: string | null,
  actorType: EntityChangeActorType,
  actorId: number | null
): string => {
  if (actorName) return actorName;
  switch (actorType) {
    case "Background":
      return "Sistemi";
    case "Customer":
      return actorId ? `Klienti #${actorId}` : "Klienti";
    case "Admin":
      return actorId ? `Admin #${actorId}` : "Admin";
    default:
      return "Anonim";
  }
};

const VERBS: Record<EntityChangeAction, [string, string]> = {
  Created: ["u krijua", "u krijuan"],
  Updated: ["u ndryshua", "u ndryshuan"],
  SoftDeleted: ["u fshi", "u fshinë"],
  Restored: ["u rikthye", "u rikthyen"],
  HardDeleted: ["u fshi përfundimisht", "u fshinë përfundimisht"]
};

// A save whose rows all replaced identical ones: the backend shows the action with no rows.
export const NO_VISIBLE_CHANGE = "Pa ndryshime";

// The one action label of a group when every row shares it, else how many rows it holds.
export const groupActionLabel = (group: IEntityChangeGroup): string => {
  if (group.rowCount === 0) return NO_VISIBLE_CHANGE;
  if (isUndo(group)) return REVERT_LABEL;
  const actions = new Set(group.rows.map((r) => r.action));
  return actions.size === 1 ? ACTION_LABELS[group.rows[0].action] : `${group.rowCount} ndryshime`;
};

export const groupActionStyle = (group: IEntityChangeGroup): string => {
  if (isUndo(group)) return REVERT_STYLE;
  const actions = new Set(group.rows.map((r) => r.action));
  return actions.size === 1 ? ACTION_STYLES[group.rows[0].action] : "bg-gray-100 text-gray-800";
};

// "Ditët e udhëtimit · 2 Ditë e veçantë e orarit u krijuan · 2 u fshinë përgjithmonë".
export const summaryLines = (group: IEntityChangeGroup): string[] => {
  if (group.rowCount === 0) return ["U ruajt, por asgjë nuk ndryshoi"];
  if (isUndo(group)) {
    const what = group.rowCount === 1 ? "ndryshimit" : `${group.rowCount} ndryshimeve`;
    return [`Kthim i ${what} të ${formatUtc(earliestUndone(group))}`];
  }
  if (group.rowCount === 1) return changeLines(group.rows[0]);

  const lines: string[] = [];
  const subjectRows = group.rows.filter(
    (r) => r.entityName === group.entityName && r.entityId === group.entityId && r.action === "Updated"
  );
  for (const row of subjectRows) lines.push(...changeLines(row));

  const others = group.summary.filter(
    (s) => !(s.entityName === group.entityName && s.action === "Updated" && subjectRows.length > 0)
  );
  for (const s of others) {
    const sentence = countedSentence(s.entityName, s.variant, s.action, s.count);
    if (sentence) {
      lines.push(sentence);
      continue;
    }
    const verb = VERBS[s.action][s.count > 1 ? 1 : 0];
    lines.push(`${countedEntity(s.entityName, s.count)} ${verb}`);
  }
  return lines;
};

// The same, as one line, for anywhere that has room for one (a tooltip).
export const summarizeGroup = (group: IEntityChangeGroup): string => summaryLines(group).join(" · ");
